import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Spinner, Table } from "react-bootstrap";
import { Input } from "components/Input/Input";
import moment from "moment";
import { showErrorToast } from "components/CustomAlerts/CustomToast";
import { CloudIcon } from "assets/icons/CloudIcon";
import { IconButton } from "components/IconButton/IconButton";
import { DeleteIcon } from "assets/icons/DeleteIcon";
import { postImportacionMasivaPci } from "services/pci";
import { postImportacionMasivaInstitucion } from "services/institucion";
import { ImportacionMasivaResponseI } from "dtos/pci";

export type ImportacionMasivaTipo = "pci" | "institucion";

interface ImportacionMasivaModalProps {
  show: boolean;
  onHide: () => void;
  tipo?: ImportacionMasivaTipo;
}

const importacionMasivaConfig = {
  pci: {
    titulo: "Importación Masiva de PCI",
    nombreDefault: "Importación masiva PCI",
    importar: postImportacionMasivaPci,
  },
  institucion: {
    titulo: "Importación Masiva de Instituciones",
    nombreDefault: "Importación masiva instituciones",
    importar: postImportacionMasivaInstitucion,
  },
};

export const ImportacionMasivaModal: React.FC<ImportacionMasivaModalProps> = ({
  show,
  onHide,
  tipo = "pci",
}) => {
  const [importName, setImportName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<ImportacionMasivaResponseI | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const importacionConfig = importacionMasivaConfig[tipo];

  useEffect(() => {
    if (show) {
      setImportName(`${importacionConfig.nombreDefault} - ${moment().format("DD/MM/YYYY HH:mm")}`);
      setFile(null);
      setResult(null);
      setIsSubmitting(false);
    }
  }, [show, importacionConfig.nombreDefault]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      validateAndSetFile(droppedFile);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const removeFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    const validExtensions = [".csv", ".xls", ".xlsx"];
    const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf(".")).toLowerCase();
    
    if (validExtensions.includes(fileExtension)) {
      setFile(selectedFile);
    } else {
      showErrorToast("Formato de archivo no válido. Por favor, suba un archivo .csv o .xls/.xlsx");
    }
  };

  const handleImport = async () => {
    if (!file) {
      showErrorToast("Debe seleccionar un archivo");
      return;
    }
    if (!importName.trim()) {
      showErrorToast("Debe ingresar un nombre para la importación");
      return;
    }

    setIsSubmitting(true);

    const params = {
      nombre: importName
    };

    try {
      const responsePost = await importacionConfig.importar({ params, images: { file } });
      if (!responsePost) {
        showErrorToast("Error al procesar la importación. Por favor, intente nuevamente.");
        return;
      }
      setResult(responsePost);
    } catch (error: any) {
      showErrorToast(error?.message || "Error al procesar la importación. Por favor, intente nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      if (result && tipo === "pci") {
        window.location.reload();
      } else {
        onHide();
      }
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>{importacionConfig.titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ position: 'relative' }}
      >
        {result ? (
          <div>
            <h5 className="mb-3 text-success">Resultados de la importación: {result.nombreImportacion}</h5>
            <h6>Resumen: {result.resumen.totalFilas} filas, {result.resumen.filasExitosas} exitosas, {result.resumen.filasConError} con error, {result.resumen.duracion}</h6>
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              <Table striped="columns" bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Estado</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errores.length ? result.errores.map((row, idx) => (
                    <tr key={idx} className={row.estado === "success" ? "" : "table-danger"}>
                      <td>{row.fila}</td>
                      <td>
                        {row.estado === "success" ? (
                          <span className="badge bg-success">Éxito</span>
                        ) : (
                          <span className="badge bg-danger">Error</span>
                        )}
                      </td>
                      <td>{row.error || "Importado correctamente"}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={3} className="text-center">
                        No se encontraron errores
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-4">
              <label className="form-label fw-bold">Nombre de la importación</label>
              <Input
                id="importName"
                values={{ importName }}
                onChange={(e: any) => setImportName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between mb-2">
                <label className="label">Archivo (CSV o XLS/XLSX)</label>
              </div>
              <div
                className={`input-file input-container position-relative ${isDragOver ? "draggin-over" : ""} ${isSubmitting ? "opacity-50" : "cursor-pointer"}`}
                onClick={() => !isSubmitting && fileInputRef.current?.click()}
              >
                <CloudIcon size={48} />
                {file ? (
                  <>
                    <p className="input-file-text fw-bold text-primary mb-0">
                      {file.name} ({(file.size / 1024).toFixed(2)} KB)
                    </p>
                    {!isSubmitting && (
                      <div className="position-absolute" style={{ right: "1rem", top: "50%", transform: "translateY(-50%)" }}>
                        <IconButton variant="error" onClick={removeFile}>
                          <DeleteIcon size={25} color="#C93B3B" />
                        </IconButton>
                      </div>
                    )}
                  </>
                ) : (
                  <div>
                    <p className="input-file-text fw-bold mb-1">Arrastre y suelte su archivo aquí</p>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="d-none"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {isSubmitting && (
              <div className="text-center mt-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Procesando archivo, por favor espere...</p>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!result ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleImport} disabled={isSubmitting || !file}>
              Importar
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleClose}>
            Cerrar
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
